import { callIdp } from './idp.ts'
import type { SignInChallenge, TokenResponse } from './types.ts'

// The IDP operations the password and second-factor flows need. Pure calls, no state — the
// same shape as endpoints.ts, so the session store stays the only stateful thing here.

export interface AuthenticationResult {
  readonly AccessToken: string
  readonly ExpiresIn: number
  readonly IdToken?: string
  readonly RefreshToken?: string
  readonly TokenType: string
}

export interface ChallengeParameters {
  /** The pool's own username (a uuid, since `username_attributes = ["email"]`). */
  readonly USER_ID_FOR_SRP?: string
  /** What a custom challenge names its user by. */
  readonly USERNAME?: string
  /** A JSON array, as a string. Non-empty means the challenge wants values we cannot invent. */
  readonly requiredAttributes?: string
}

export interface AuthResponse {
  readonly AuthenticationResult?: AuthenticationResult
  readonly ChallengeName?: string
  readonly Session?: string
  readonly ChallengeParameters?: ChallengeParameters
}

export interface CodeDeliveryDetails {
  readonly AttributeName?: string
  readonly DeliveryMedium?: string
  readonly Destination?: string
}

export interface ForgotPasswordResponse {
  readonly CodeDeliveryDetails?: CodeDeliveryDetails
}

export interface AssociateSoftwareTokenResponse {
  /** The base32 shared secret, to be typed into an authenticator or read from a QR. */
  readonly SecretCode?: string
}

export interface VerifySoftwareTokenResponse {
  readonly Status?: string
}

export interface GetUserResponse {
  /** The factors the user has verified. Empty or absent means no second factor. */
  readonly UserMFASettingList?: readonly string[]
}

/** Cognito's PascalCase result, as the kernel's token shape. */
export function toTokenResponse(result: AuthenticationResult): TokenResponse {
  return {
    access_token: result.AccessToken,
    expires_in: result.ExpiresIn,
    id_token: result.IdToken,
    refresh_token: result.RefreshToken,
    token_type: result.TokenType,
  }
}

/** No password and no SRP: Define asks the one custom challenge (infra/modules/cognito/triggers). */
export function initiateCustomAuth(clientId: string, username: string): Promise<AuthResponse> {
  return callIdp('InitiateAuth', {
    AuthFlow: 'CUSTOM_AUTH',
    ClientId: clientId,
    AuthParameters: { USERNAME: username },
  })
}

/** The API's proof travels in `ANSWER`: `ClientMetadata` of `InitiateAuth` never reaches the triggers. */
export function respondToCustomChallenge(
  clientId: string,
  challenge: { readonly session: string; readonly username: string; readonly answer: string },
): Promise<AuthResponse> {
  return callIdp(
    'RespondToAuthChallenge',
    {
      ClientId: clientId,
      ChallengeName: 'CUSTOM_CHALLENGE',
      Session: challenge.session,
      ChallengeResponses: { USERNAME: challenge.username, ANSWER: challenge.answer },
    },
    // Verify said no: an expired proof, or one for somebody else.
    { NotAuthorizedException: 'delegation_refused' },
  )
}

/** One refresh token dies, on this device only: `GlobalSignOut` would take every device of the user.
 *  Cognito answers `{}`: a caller waits for the outcome, not the body. */
export async function revokeToken(clientId: string, refreshToken: string): Promise<void> {
  await callIdp('RevokeToken', { Token: refreshToken, ClientId: clientId })
}

export function initiateUserPasswordAuth(
  clientId: string,
  credentials: { readonly email: string; readonly password: string },
): Promise<AuthResponse> {
  return callIdp('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: clientId,
    AuthParameters: { USERNAME: credentials.email, PASSWORD: credentials.password },
  })
}

/** `USERNAME` is the challenge's `USER_ID_FOR_SRP`; the email typed at sign-in is rejected. */
export function respondToNewPasswordRequired(
  clientId: string,
  challenge: SignInChallenge,
  newPassword: string,
): Promise<AuthResponse> {
  return callIdp(
    'RespondToAuthChallenge',
    {
      ClientId: clientId,
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      Session: challenge.session,
      ChallengeResponses: { USERNAME: challenge.username, NEW_PASSWORD: newPassword },
    },
    // Here a refusal is the three-minute session having died, not a wrong password.
    { NotAuthorizedException: 'challenge_expired' },
  )
}

/** The second factor at sign-in. Same shape as the first-access answer, another code name. */
export function respondToSoftwareTokenMfa(
  clientId: string,
  challenge: SignInChallenge,
  code: string,
): Promise<AuthResponse> {
  return callIdp(
    'RespondToAuthChallenge',
    {
      ClientId: clientId,
      ChallengeName: 'SOFTWARE_TOKEN_MFA',
      Session: challenge.session,
      ChallengeResponses: { USERNAME: challenge.username, SOFTWARE_TOKEN_MFA_CODE: code },
    },
    // A wrong code is CodeMismatchException; a refusal here is the spent session.
    { NotAuthorizedException: 'challenge_expired' },
  )
}

export function changePassword(
  accessToken: string,
  passwords: { readonly current: string; readonly next: string },
): Promise<void> {
  return callIdp('ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: passwords.current,
    ProposedPassword: passwords.next,
  })
}

export function forgotPassword(clientId: string, email: string): Promise<ForgotPasswordResponse> {
  return callIdp('ForgotPassword', { ClientId: clientId, Username: email })
}

export function confirmForgotPassword(
  clientId: string,
  input: { readonly email: string; readonly code: string; readonly newPassword: string },
): Promise<void> {
  return callIdp(
    'ConfirmForgotPassword',
    {
      ClientId: clientId,
      Username: input.email,
      ConfirmationCode: input.code,
      Password: input.newPassword,
    },
    // No credential is in play: a refusal means the reset window is spent, and the fix is a
    // new code rather than a corrected password.
    { NotAuthorizedException: 'code_expired' },
  )
}

/** Every device of the user. Cognito answers `{}`, as for `revokeToken`. */
export async function globalSignOut(accessToken: string): Promise<void> {
  await callIdp('GlobalSignOut', { AccessToken: accessToken })
}

export function associateSoftwareToken(
  accessToken: string,
): Promise<AssociateSoftwareTokenResponse> {
  return callIdp('AssociateSoftwareToken', { AccessToken: accessToken })
}

/** `FriendlyDeviceName` is what the pool reports back as the authenticator's label. */
export function verifySoftwareToken(
  accessToken: string,
  input: { readonly code: string; readonly deviceName: string },
): Promise<VerifySoftwareTokenResponse> {
  return callIdp('VerifySoftwareToken', {
    AccessToken: accessToken,
    UserCode: input.code,
    FriendlyDeviceName: input.deviceName,
  })
}

export function setSoftwareTokenMfaPreference(
  accessToken: string,
  enabled: boolean,
): Promise<void> {
  return callIdp('SetUserMFAPreference', {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: { Enabled: enabled, PreferredMfa: enabled },
  })
}

export function getUser(accessToken: string): Promise<GetUserResponse> {
  return callIdp('GetUser', { AccessToken: accessToken })
}
